/**
 * whiteboardMerge — the versioned diff/merge engine (the core round-trip).
 *
 * This is the heart of "take v1, work on it physically, save as v2." A board
 * lives digitally as a BoardBaseline (the merge base). When it's recaptured
 * from a whiteboard, we diff the freshly recognized scene against that baseline
 * and classify every element:
 *
 *   items:         unchanged · new · moved · renamed · (removed)
 *   relationships: unchanged · new · retyped · (removed)
 *
 * producing v2 that PRESERVES v1's identities — a box you only drew *beside*
 * the old diagram becomes a new item wired into the existing ones (no clones),
 * and a box you renamed/moved updates the *same* item rather than duplicating.
 *
 * Matching is anchor-less here (title first, then position proximity for
 * renames), which is enough to prove the concept; printed anchor codes /
 * projection would later make re-identification exact. Pure & deterministic.
 */

import {
  findDuplicateMatch,
  extractSceneRelationships,
  altitudeFromPosition,
  DEFAULT_COLOR_TO_CATEGORY,
  type WhiteboardScene,
  type WhiteboardNode,
  type WhiteboardImportPlan,
  type RelationshipInferenceBasis,
} from './whiteboardImportService';
import { resolveSignifierPatch } from './whiteboardSignifiers';
import type { MiroColorRule } from './fieldMappingRegistry';
import type { RelationshipEdgeType, RelationshipDirectionality } from '../types/prioritization/itemRelationships';
import type { ItemLanguageType } from '../types/prioritization/lifecycle';
import type { AltitudeLevel } from '../types/prioritization/itemLifecycle';

// ── Baseline (v1, the merge base) ────────────────────────────────────────────

export interface BaselineItem {
  /** Stable platform item id. */
  id: string;
  title: string;
  /** Normalized board position, origin top-left, x/y in [0,1]. */
  position: { x: number; y: number };
  /**
   * Short anchor code written on the physical board (e.g. "K7"). When present
   * it's the exact match key across versions — survives rename and move.
   */
  code?: string;
}

export interface BaselineRelationship {
  sourceItemId: string;
  targetItemId: string;
  type: RelationshipEdgeType;
  directionality: RelationshipDirectionality;
}

export interface BoardBaseline {
  versionId: string;
  capturedAt: string;
  items: BaselineItem[];
  relationships: BaselineRelationship[];
}

// ── Merge result (v2) ────────────────────────────────────────────────────────

export type ItemChangeStatus = 'unchanged' | 'new' | 'moved' | 'renamed';
export type RelationshipChangeStatus = 'unchanged' | 'new' | 'retyped';

export interface MergedItem {
  itemId: string;
  title: string;
  position: { x: number; y: number };
  status: ItemChangeStatus;
  /** For renamed items, the v1 title. */
  previousTitle?: string;
  /** Anchor code carried from v1 (or freshly assigned for a new item). */
  code?: string;
  /** True when this item was matched to v1 exactly by its anchor code. */
  matchedByCode?: boolean;
  // Attributes an apply needs to write. Populated for 'new' (all) and 'moved'
  // (altitude only); omitted where nothing changes.
  color?: string;
  itemCategory?: ItemLanguageType;
  altitudeLevel?: AltitudeLevel;
  /** Attribute values assigned by the workspace's visual-language rules (new items only). */
  metadata?: Record<string, string | number>;
}

export interface MergedRelationship {
  sourceItemId: string;
  targetItemId: string;
  type: RelationshipEdgeType;
  directionality: RelationshipDirectionality;
  status: RelationshipChangeStatus;
  /** For retyped relationships, the v1 type. */
  previousType?: RelationshipEdgeType;
  /**
   * Uncertainty of the type inference, carried through the recapture path for
   * parity with fresh import (set 2). Present on new/retyped relationships.
   */
  confidence?: number;
  basis?: RelationshipInferenceBasis;
}

export interface MergeResult {
  items: MergedItem[];
  removedItems: BaselineItem[];
  relationships: MergedRelationship[];
  removedRelationships: BaselineRelationship[];
  summary: {
    unchanged: number;
    added: number;
    moved: number;
    renamed: number;
    removed: number;
    relationshipsAdded: number;
    relationshipsRetyped: number;
    relationshipsRemoved: number;
  };
}

export interface MergeOptions {
  /** Position delta (normalized) above which a re-identified item counts as "moved". */
  moveThreshold?: number;
  /** Position proximity (normalized) within which an unmatched box is treated as a rename of a nearby v1 item. */
  renameProximity?: number;
  /**
   * The workspace's visual-language rules. Applied to NEW items only (existing
   * items keep their attributes so a re-capture doesn't clobber edits made in
   * the app), mirroring buildImportPlan.
   */
  signifierRules?: MiroColorRule[];
}

export const MOVE_DISTANCE_THRESHOLD = 0.08;
export const RENAME_PROXIMITY_THRESHOLD = 0.06;

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `wb-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

// Unambiguous, hand-writable alphabet (no I/O/S/0/1/5 lookalikes).
const ANCHOR_ALPHABET = 'ABCDEFGHJKLMNPQRTUVWXY2346789';

/** Generate a short anchor code not already in `existing` (and reserve it). */
export function generateAnchorCode(existing: Set<string>): string {
  for (let len = 2; len <= 4; len++) {
    for (let attempt = 0; attempt < 200; attempt++) {
      let code = '';
      for (let i = 0; i < len; i++) code += ANCHOR_ALPHABET[Math.floor(Math.random() * ANCHOR_ALPHABET.length)];
      if (!existing.has(code)) { existing.add(code); return code; }
    }
  }
  const fallback = `X${Date.now().toString(36).toUpperCase().slice(-3)}`;
  existing.add(fallback);
  return fallback;
}

function sameTitle(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function nodeTitle(node: WhiteboardNode): string {
  const lines = node.text.split('\n').map((l) => l.trim()).filter(Boolean);
  return (lines[0] || node.text.trim() || 'Untitled').slice(0, 200);
}

function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Diff a freshly captured scene against a board baseline and produce v2.
 *
 * Three passes resolve each captured node to an item id:
 *   1. title match → existing item (moved if it shifted, else unchanged)
 *   2. unmatched box near an unclaimed v1 item → rename of that item
 *   3. still unmatched → a brand-new item
 * Leftover v1 items are reported as removed (never auto-deleted).
 */
export function mergeCapturedScene(
  baseline: BoardBaseline,
  scene: WhiteboardScene,
  options: MergeOptions = {},
): MergeResult {
  const moveThreshold = options.moveThreshold ?? MOVE_DISTANCE_THRESHOLD;
  const renameProximity = options.renameProximity ?? RENAME_PROXIMITY_THRESHOLD;
  const signifierRules = options.signifierRules ?? [];

  const usedBaseline = new Set<string>();
  const nodeToItemId = new Map<string, string>();
  const items: MergedItem[] = [];

  // Codes already in use, plus a code → baseline-item lookup for exact matching.
  const usedCodes = new Set<string>();
  const baselineByCode = new Map<string, BaselineItem>();
  for (const b of baseline.items) {
    if (b.code) {
      const c = b.code.toUpperCase();
      usedCodes.add(c);
      baselineByCode.set(c, b);
    }
  }

  // Pass 0 — exact match by anchor code. Survives rename AND move.
  const afterCode: WhiteboardNode[] = [];
  for (const node of scene.nodes) {
    const code = node.code?.toUpperCase();
    const base = code ? baselineByCode.get(code) : undefined;
    if (!code || !base || usedBaseline.has(base.id)) {
      afterCode.push(node);
      continue;
    }
    usedBaseline.add(base.id);
    nodeToItemId.set(node.id, base.id);
    const title = nodeTitle(node);
    const renamed = !sameTitle(title, base.title);
    const moved = distance(node.position, base.position) > moveThreshold;
    items.push({
      itemId: base.id,
      title: renamed ? title : base.title,
      position: node.position,
      status: renamed ? 'renamed' : moved ? 'moved' : 'unchanged',
      code: base.code,
      matchedByCode: true,
      ...(renamed && { previousTitle: base.title }),
      ...(moved && { altitudeLevel: altitudeFromPosition(node.position.y) }),
    });
  }

  // Pass 1 — title match against the baseline
  const unmatched: WhiteboardNode[] = [];
  for (const node of afterCode) {
    const title = nodeTitle(node);
    const candidates = baseline.items
      .filter((i) => !usedBaseline.has(i.id))
      .map((i) => ({ id: i.id, title: i.title }));
    const match = findDuplicateMatch(title, candidates);
    if (!match) {
      unmatched.push(node);
      continue;
    }
    const base = baseline.items.find((i) => i.id === match.existingItemId)!;
    usedBaseline.add(base.id);
    nodeToItemId.set(node.id, base.id);
    const moved = distance(node.position, base.position) > moveThreshold;
    items.push({
      itemId: base.id,
      title: base.title,
      position: node.position,
      status: moved ? 'moved' : 'unchanged',
      code: base.code,
      // A moved box implies a new altitude (board height = altitude).
      ...(moved && { altitudeLevel: altitudeFromPosition(node.position.y) }),
    });
  }

  // Pass 2 — rename: an unmatched box sitting where a still-unclaimed v1 item was.
  // A node carrying its own anchor code is authoritative identity: if its code
  // didn't match in Pass 0, it's a different box — don't position-match it.
  const stillUnmatched: WhiteboardNode[] = [];
  for (const node of unmatched) {
    if (node.code) {
      stillUnmatched.push(node);
      continue;
    }
    let best: { base: BaselineItem; d: number } | null = null;
    for (const base of baseline.items) {
      if (usedBaseline.has(base.id)) continue;
      const d = distance(node.position, base.position);
      if (d <= renameProximity && (!best || d < best.d)) best = { base, d };
    }
    if (!best) {
      stillUnmatched.push(node);
      continue;
    }
    usedBaseline.add(best.base.id);
    nodeToItemId.set(node.id, best.base.id);
    items.push({
      itemId: best.base.id,
      title: nodeTitle(node),
      previousTitle: best.base.title,
      position: node.position,
      status: 'renamed',
      code: best.base.code,
    });
  }

  // Pass 3 — genuinely new items (same signifier mapping as a fresh import)
  for (const node of stillUnmatched) {
    const itemId = newId();
    nodeToItemId.set(node.id, itemId);
    const color = (node.color || 'unknown').toLowerCase();
    // The workspace's visual language wins where it assigns a value, exactly as
    // in buildImportPlan, so a box drawn beside the projection imports its rich
    // attributes too.
    const patch = signifierRules.length
      ? resolveSignifierPatch(
          { fillColor: node.fillColor ?? color, borderColor: node.borderColor, textColor: node.textColor, shape: node.shape },
          signifierRules,
        )
      : null;
    items.push({
      itemId,
      title: nodeTitle(node),
      position: node.position,
      status: 'new',
      color,
      code: node.code?.toUpperCase() || generateAnchorCode(usedCodes),
      itemCategory: patch?.itemCategory ?? DEFAULT_COLOR_TO_CATEGORY[color],
      altitudeLevel: patch?.altitudeLevel ?? altitudeFromPosition(node.position.y),
      ...(patch?.metadata && Object.keys(patch.metadata).length ? { metadata: patch.metadata } : {}),
    });
  }

  // Backfill anchor codes for any item that still lacks one (legacy v1 boards
  // imported before codes existed) so v2 carries a complete code set.
  for (const it of items) {
    if (!it.code) it.code = generateAnchorCode(usedCodes);
    else usedCodes.add(it.code.toUpperCase());
  }

  const removedItems = baseline.items.filter((i) => !usedBaseline.has(i.id));

  // ── Relationships ──────────────────────────────────────────────────────────
  const baselineRels = baseline.relationships.map((r) => ({ rel: r, used: false }));
  const findBaselineRel = (src: string, tgt: string, undirected: boolean) =>
    baselineRels.find(
      (b) =>
        !b.used &&
        ((b.rel.sourceItemId === src && b.rel.targetItemId === tgt) ||
          ((undirected || b.rel.directionality === 'undirected') &&
            b.rel.sourceItemId === tgt &&
            b.rel.targetItemId === src)),
    );
  /**
   * Boards captured before Sequence & conditions orientation stored dependency
   * arrows with visual endpoints unflipped. Match the reverse pair so re-import
   * can retype/reorient instead of stacking a duplicate.
   */
  const findReversedSequenceRel = (src: string, tgt: string) =>
    baselineRels.find(
      (b) =>
        !b.used &&
        (b.rel.type === 'dependency' || b.rel.type === 'prerequisite') &&
        b.rel.sourceItemId === tgt &&
        b.rel.targetItemId === src,
    );

  const relationships: MergedRelationship[] = [];
  for (const sceneRel of extractSceneRelationships(scene, signifierRules)) {
    const src = nodeToItemId.get(sceneRel.sourceNodeId);
    const tgt = nodeToItemId.get(sceneRel.targetNodeId);
    if (!src || !tgt || src === tgt) continue;

    const match = findBaselineRel(src, tgt, sceneRel.directionality === 'undirected');
    if (!match) {
      const reversed =
        (sceneRel.type === 'dependency' || sceneRel.type === 'prerequisite')
          ? findReversedSequenceRel(src, tgt)
          : undefined;
      if (reversed) {
        reversed.used = true;
        relationships.push({
          sourceItemId: src,
          targetItemId: tgt,
          type: sceneRel.type,
          directionality: sceneRel.directionality,
          previousType: reversed.rel.type,
          status: 'retyped',
          confidence: sceneRel.confidence,
          basis: sceneRel.basis,
        });
        continue;
      }
      relationships.push({
        sourceItemId: src,
        targetItemId: tgt,
        type: sceneRel.type,
        directionality: sceneRel.directionality,
        status: 'new',
        confidence: sceneRel.confidence,
        basis: sceneRel.basis,
      });
      continue;
    }
    match.used = true;
    if (match.rel.type === sceneRel.type) {
      relationships.push({
        sourceItemId: src,
        targetItemId: tgt,
        type: sceneRel.type,
        directionality: sceneRel.directionality,
        status: 'unchanged',
      });
    } else {
      relationships.push({
        sourceItemId: src,
        targetItemId: tgt,
        type: sceneRel.type,
        directionality: sceneRel.directionality,
        previousType: match.rel.type,
        status: 'retyped',
        confidence: sceneRel.confidence,
        basis: sceneRel.basis,
      });
    }
  }

  const removedRelationships = baselineRels.filter((b) => !b.used).map((b) => b.rel);

  return {
    items,
    removedItems,
    relationships,
    removedRelationships,
    summary: {
      unchanged: items.filter((i) => i.status === 'unchanged').length,
      added: items.filter((i) => i.status === 'new').length,
      moved: items.filter((i) => i.status === 'moved').length,
      renamed: items.filter((i) => i.status === 'renamed').length,
      removed: removedItems.length,
      relationshipsAdded: relationships.filter((r) => r.status === 'new').length,
      relationshipsRetyped: relationships.filter((r) => r.status === 'retyped').length,
      relationshipsRemoved: removedRelationships.length,
    },
  };
}

/**
 * Snapshot an import plan as a board baseline (v1) — the merge base a later
 * recapture diffs against. Uses each item's resolved id (created or reused).
 */
export function boardBaselineFromPlan(plan: WhiteboardImportPlan, versionId?: string): BoardBaseline {
  const refToItemId = new Map<string, string>();
  for (const it of plan.items) {
    if (!it.include) continue;
    const id = it.resolution === 'use_existing' && it.duplicateOf ? it.duplicateOf.existingItemId : it.itemId;
    refToItemId.set(it.ref, id);
  }

  const usedCodes = new Set<string>();
  const items: BaselineItem[] = plan.items
    .filter((it) => it.include)
    .map((it) => ({
      id: refToItemId.get(it.ref)!,
      title: it.title,
      position: it.position,
      code: generateAnchorCode(usedCodes),
    }));

  const relationships: BaselineRelationship[] = [];
  for (const r of plan.relationships) {
    if (!r.include) continue;
    const src = refToItemId.get(r.sourceRef);
    const tgt = refToItemId.get(r.targetRef);
    if (!src || !tgt || src === tgt) continue;
    relationships.push({ sourceItemId: src, targetItemId: tgt, type: r.type, directionality: r.directionality });
  }

  return {
    versionId: versionId ?? newId(),
    capturedAt: new Date().toISOString(),
    items,
    relationships,
  };
}
